import React, { useState, useEffect } from "react";
import {
  Download,
  FileText,
  RefreshCw,
  Calendar,
  Receipt,
  CreditCard,
  Sparkles,
  Wallet,
  Workflow,
  ChevronLeft,
  ChevronRight,
  Loader,
} from "lucide-react";
import { listInvoices, downloadInvoice } from "../apis/invoice";
import { toast } from "react-toastify";

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const limit = 12;

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadInvoices = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const response = await listInvoices(page, limit);
      const data = response.data?.data || {};
      setInvoices(data.invoices || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error loading invoices:", error);
      if (!silent) toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoice(invoice.id, `${invoice.invoice_number}.pdf`);
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount || amount === 0) return "Free";
    return `Rs. ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const invoiceLabel = (invoice) => {
    if (invoice.plan_name === "wallet_credit_admin") return "Wallet credit (admin)";
    if (invoice.plan_name === "wallet_recharge") return "Wallet recharge";
    return invoice.plan_name || "-";
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "plan":
        return { label: "Plan", chip: "b", icon: CreditCard };
      case "addon":
        return { label: "Add-on", chip: "p", icon: Workflow };
      case "ai_credits":
        return { label: "AI Credits", chip: "g", icon: Sparkles };
      case "wallet":
        return { label: "Wallet", chip: "n", icon: Wallet };
      default:
        return { label: type || "Other", chip: "n", icon: FileText };
    }
  };

  const filteredInvoices =
    filter === "all"
      ? invoices
      : invoices.filter((inv) => inv.type === filter);

  const typeStats = {
    plan: invoices.filter((i) => i.type === "plan").length,
    addon: invoices.filter((i) => i.type === "addon").length,
    ai_credits: invoices.filter((i) => i.type === "ai_credits").length,
    wallet: invoices.filter((i) => i.type === "wallet").length,
  };

  return (
    <div className="invoices-page">
      <div className="page-head">
        <div>
          <h2>Invoices</h2>
          <p>View and download your purchase invoices</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          onClick={() => loadInvoices(true)}
          disabled={refreshing}
          className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="inv-kpis">
        <div className="inv-kpi hi">
          <div>
            <div className="k">Total invoices</div>
            <div className="v">{total}</div>
          </div>
          <div className="ico"><Receipt size={14} /></div>
        </div>
        <div className="inv-kpi">
          <div>
            <div className="k">Plans</div>
            <div className="v">{typeStats.plan}</div>
          </div>
          <div className="ico"><CreditCard size={14} /></div>
        </div>
        <div className="inv-kpi">
          <div>
            <div className="k">Add-ons</div>
            <div className="v">{typeStats.addon}</div>
          </div>
          <div className="ico"><Workflow size={14} /></div>
        </div>
        <div className="inv-kpi">
          <div>
            <div className="k">AI Credits</div>
            <div className="v">{typeStats.ai_credits}</div>
          </div>
          <div className="ico"><Sparkles size={14} /></div>
        </div>
        <div className="inv-kpi">
          <div>
            <div className="k">Wallet</div>
            <div className="v">{typeStats.wallet}</div>
          </div>
          <div className="ico"><Wallet size={14} /></div>
        </div>
      </div>

      <div className="ping-pill-tabs">
        {[
          { key: "all", label: `All (${invoices.length})` },
          { key: "plan", label: `Plans (${typeStats.plan})` },
          { key: "addon", label: `Add-ons (${typeStats.addon})` },
          { key: "ai_credits", label: `AI Credits (${typeStats.ai_credits})` },
          { key: "wallet", label: `Wallet (${typeStats.wallet})` },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setFilter(tab.key)}
            className={`ping-ptab ${filter === tab.key ? "on" : ""}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="inv-table-card inv-empty">
          <Loader className="animate-spin" size={22} style={{ color: "var(--green-deep)" }} />
          <p>Loading invoices…</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="inv-table-card inv-empty">
          <Receipt size={28} />
          <h3>No invoices found</h3>
          <p>
            {filter !== "all"
              ? `No ${filter === "ai_credits" ? "AI Credits" : filter} invoices available`
              : "Purchase a plan or add money to your wallet to generate your first invoice"}
          </p>
        </div>
      ) : (
        <div className="inv-table-card">
          <div className="hidden md:block overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th className="num">Amount</th>
                  <th className="act">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => {
                  const badge = getTypeBadge(invoice.type);
                  const BadgeIcon = badge.icon;
                  return (
                    <tr key={invoice.id}>
                      <td>
                        <div className="inv-id">
                          <FileText size={13} />
                          <span>{invoice.invoice_number}</span>
                        </div>
                      </td>
                      <td>
                        <div className="inv-date">
                          <Calendar size={12} />
                          {formatDate(invoice.invoice_date || invoice.created_at)}
                        </div>
                      </td>
                      <td>
                        <span className={`ping-chip ${badge.chip}`}>
                          <BadgeIcon size={11} />
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        <span className="inv-desc">{invoiceLabel(invoice)}</span>
                      </td>
                      <td className="num">
                        <div className="inv-amt">{formatAmount(invoice.total)}</div>
                        {invoice.gst_amount > 0 && (
                          <div className="inv-gst">incl. GST {formatAmount(invoice.gst_amount)}</div>
                        )}
                      </td>
                      <td className="act">
                        <button
                          type="button"
                          onClick={() => handleDownload(invoice)}
                          disabled={downloadingId === invoice.id}
                          className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
                        >
                          {downloadingId === invoice.id ? (
                            <Loader size={13} className="animate-spin" />
                          ) : (
                            <Download size={13} />
                          )}
                          Download
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden inv-mobile">
            {filteredInvoices.map((invoice) => {
              const badge = getTypeBadge(invoice.type);
              const BadgeIcon = badge.icon;
              return (
                <div key={invoice.id} className="inv-mrow">
                  <div className="inv-mrow-top">
                    <div>
                      <div className="inv-id">
                        <FileText size={13} />
                        <span>{invoice.invoice_number}</span>
                      </div>
                      <div className="inv-date">
                        {formatDate(invoice.invoice_date || invoice.created_at)}
                      </div>
                    </div>
                    <span className={`ping-chip ${badge.chip}`}>
                      <BadgeIcon size={11} />
                      {badge.label}
                    </span>
                  </div>
                  <p className="inv-desc">{invoiceLabel(invoice)}</p>
                  <div className="inv-mrow-bot">
                    <div>
                      <div className="inv-amt">{formatAmount(invoice.total)}</div>
                      {invoice.gst_amount > 0 && (
                        <div className="inv-gst">GST {formatAmount(invoice.gst_amount)}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDownload(invoice)}
                      disabled={downloadingId === invoice.id}
                      className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
                    >
                      {downloadingId === invoice.id ? (
                        <Loader size={12} className="animate-spin" />
                      ) : (
                        <Download size={12} />
                      )}
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="inv-pager">
              <p>
                Page {page} of {totalPages} ({total} invoices)
              </p>
              <div className="inv-pager-btns">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Invoices;
